/**
 * The render surface, and the mobile render budget every later change inherits.
 *
 * The settings here are not defaults to revisit — they are the budget from the design
 * doc. The target is a stable frame rate on a mid-range phone, and each of these is a
 * deliberate trade of fidelity for frames:
 *
 * - `dpr` clamped to 1.5 — the single biggest win on a phone. A modern handset reports a
 *   device pixel ratio of 3, which means rendering nine times the pixels for a difference
 *   almost nobody can see at arm's length.
 * - MSAA on — traded back deliberately. This art is flat colour meeting flat colour along
 *   long straight architectural edges, which is the one case where aliasing is impossible
 *   not to see and where multisampling is at its cheapest: it does extra work only at
 *   triangle edges, and a tiler resolves it in tile memory. Post-process AA (FXAA, SMAA)
 *   was the alternative and is worse on both counts — it costs a full-screen pass through
 *   an offscreen target, the expensive shape on a tiler, and it softens flat regions that
 *   have no aliasing to fix. Unmeasured on a handheld, like the lighting budget below.
 * - exactly one shadow-casting light, and its shadow camera is fitted to the level
 *   rather than fixed (see `LevelLighting`) — sharper shadows for free.
 * - no post-processing at all.
 * - outdoors, the one shadow map is rendered a few times and then frozen — a maze has no
 *   moving casters at all, so every frame after the first is redrawing the same picture
 *   (see `FrozenShadows`).
 * - everything is compiled and uploaded at boot rather than on first sight, which trades a
 *   longer load for no first-look hitches (see `Preload` below).
 */

import { Preload } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { type ReactNode } from 'react';
import { CAMERA_FAR, CAMERA_FOV, CAMERA_NEAR, EYE_HEIGHT } from './constants';
import { FrozenShadows } from './FrozenShadows';

type Props = {
  children: ReactNode;
  /** Outdoor levels get the longer fog range below. See `OUTDOOR_FOG`. */
  outdoors?: boolean;
  /**
   * Whether the level has been built and mounted. `Preload` compiles whatever is in the
   * scene at the moment it mounts, and for the first second of the game's life that is
   * nothing at all — the profile is still loading. Mounting it on this flag instead of on
   * the canvas is the difference between preloading the world and preloading an empty
   * scene.
   */
  ready?: boolean;
  /**
   * Identifies the world currently mounted below — the level id. Crossing a portal swaps
   * one maze for another without changing anything else the canvas can see, and the
   * frozen shadow map has to be redrawn for the maze the player arrived in. See
   * `FrozenShadows`.
   */
  sceneKey?: string;
};

/**
 * Mild depth haze; also softens the far end of long rooms. Indoors nothing is more than a
 * few metres away, so this is nearly all atmosphere and a little concealment.
 */
const INDOOR_FOG: [string, number, number] = ['#141821', 14, 45];

/**
 * Outdoors it is doing the opposite job, and the indoor range does it badly.
 *
 * A grounds level is ~48 m of lawn with scenery arranged behind it out to a horizon at
 * 60 m; at a fog that reaches full strength by 45 m, everything past the moat is one flat
 * wash and the house on the far bank simply is not there. Running it to 65 m instead puts
 * full fog just beyond the furthest thing that is fogged at all — the backdrop, the woods
 * and the moon are all exempt — so the house greys with distance instead of vanishing, and
 * the far side of the maze is still hazy enough that the jump-glimpse does not hand the
 * player the solution (design D5).
 *
 * The near edge moves out a little for the same reason: 14 m indoors is the far wall of a
 * room, and outdoors it is four hedges away.
 */
const OUTDOOR_FOG: [string, number, number] = ['#141821', 18, 65];

export function GameCanvas({ children, outdoors = false, ready = false, sceneKey = '' }: Props) {
  return (
    <Canvas
      dpr={[1, 1.5]}
      // Not "basic": BasicShadowMap is unfiltered, so every shadow edge is a hard
      // staircase of shadow-map texels however large the map is. PCF costs a few extra
      // taps per shadowed fragment, which is affordable here precisely because so
      // little casts — only props and doors do (see LevelGeometry's shadow policy).
      shadows="soft"
      gl={{
        antialias: true,
        powerPreference: 'high-performance',
        stencil: false,
        depth: true,
      }}
      camera={{
        fov: CAMERA_FOV,
        near: CAMERA_NEAR,
        far: CAMERA_FAR,
        position: [0, EYE_HEIGHT, 0],
      }}
    >
      <color attach="background" args={['#141821']} />
      <fog attach="fog" args={outdoors ? OUTDOOR_FOG : INDOOR_FOG} />

      {/* The ambient floor, and the only light that lives here: it needs no level to
          place it. It does two jobs. It keeps the places the room lamps do not reach
          from going pitch black, which is what the visual-style spec requires of a room
          whose lamp has been switched off. And it stands in for bounce: there is no
          global illumination in this renderer, so nothing else fills the shadow side of
          a wall, and without it every unlit face reads as a flat cut-out. The ground
          colour is warm on purpose — light reaching a wall from below has come off a
          wooden floor, not out of the sky. */}
      <hemisphereLight args={['#8ea3bd', '#6d5a43', 0.55]} />

      {children}

      {/* Compiles every shader and uploads every texture the moment the level exists,
          instead of the first time the moon, the backdrop house or a far corner of the maze
          swings into view. Those first-sight hitches are the stutter players actually
          report while moving, and they cost nothing to remove: this is one traversal and a
          128² cube render at boot, once, on both profiles. It mounts with the level rather
          than with the canvas — see `ready`. */}
      {ready && <Preload all />}
      {ready && outdoors && <FrozenShadows sceneKey={sceneKey} />}
    </Canvas>
  );
}
